import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Clock, Shield } from 'lucide-react';
import { useCalibrationSummary } from '@/features/intelligence/hooks/useCalibrationCertificates';
import { getCalibrationCertificates } from '@/features/intelligence/api';
import { getCertificateStatus, formatCertificateDate } from '@/features/intelligence/utils/certificateStatus';
import type { CalibrationCertificate } from '@/features/intelligence/types';

interface CalibrationSummaryCardProps {
  accountId: string;
}

export default function CalibrationSummaryCard({ accountId }: CalibrationSummaryCardProps) {
  const summaryQuery = useCalibrationSummary({ accountId });

  const needsAttention = summaryQuery.data
    ? summaryQuery.data.overdue + summaryQuery.data.dueIn30Days + summaryQuery.data.dueIn60Days + summaryQuery.data.dueIn90Days
    : 0;

  const urgentQuery = useQuery({
    queryKey: ['account-calibration-urgent', accountId],
    queryFn: () =>
      getCalibrationCertificates({
        accountId,
        pageLength: 5,
        sortKey: 'days_until_due',
        sortDirection: 1,
        dueInDays: 90,
        includeOverdue: true,
      }),
    enabled: !!accountId && !summaryQuery.isLoading && needsAttention > 0,
    staleTime: 5 * 60 * 1000,
  });

  if (summaryQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Calibration Certificates
          </CardTitle>
          <CardDescription>Sensors requiring calibration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (summaryQuery.error || !summaryQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Calibration Certificates
          </CardTitle>
          <CardDescription>Sensors requiring calibration</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Unable to load calibration data
          </p>
        </CardContent>
      </Card>
    );
  }

  const summary = summaryQuery.data;
  const urgentCerts = urgentQuery.data?.items || [];

  // All certificates are beyond 90 days or there are none
  if (needsAttention === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Calibration Certificates
          </CardTitle>
          <CardDescription>Sensors requiring calibration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm font-medium">All certificates up to date</span>
          </div>
          <Link
            to={`/intelligence/calibration-certificates?accountId=${accountId}`}
            className="text-xs text-primary hover:underline mt-2 block"
          >
            View all certificates &rarr;
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-orange-500" />
              Calibration Certificates
            </CardTitle>
            <CardDescription>
              {needsAttention} certificate{needsAttention !== 1 ? 's' : ''} due within 90 days
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {summary.overdue > 0 && (
            <Badge variant="destructive" className="text-xs">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {summary.overdue} Overdue
            </Badge>
          )}
          {summary.dueIn30Days > 0 && (
            <Badge className="text-xs bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200">
              <Clock className="h-3 w-3 mr-1" />
              {summary.dueIn30Days} Due in 30d
            </Badge>
          )}
          {summary.dueIn60Days > 0 && (
            <Badge className="text-xs bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-200">
              {summary.dueIn60Days} Due in 60d
            </Badge>
          )}
          {summary.dueIn90Days > 0 && (
            <Badge className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200">
              {summary.dueIn90Days} Due in 90d
            </Badge>
          )}
        </div>

        {/* Urgent certificates list */}
        {urgentCerts.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Most Urgent</p>
            {urgentCerts.map((cert) => (
              <CertificateItem key={cert.id} cert={cert} />
            ))}
          </div>
        )}

        <Link
          to={`/intelligence/calibration-certificates?accountId=${accountId}`}
          className="text-xs text-primary hover:underline mt-3 block"
        >
          View all certificates &rarr;
        </Link>
      </CardContent>
    </Card>
  );
}

function CertificateItem({ cert }: { cert: CalibrationCertificate }) {
  const statusInfo = getCertificateStatus(cert.daysUntilDue);

  return (
    <div className="flex items-start justify-between p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium truncate" title={cert.streamName || cert.equipmentName || cert.certNumber}>
            {cert.streamName || cert.equipmentName || cert.certNumber}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground">
            {cert.siteName || 'Unknown site'}
          </span>
          <span className="text-xs text-muted-foreground">
            &bull; Due {formatCertificateDate(cert.dueDate)}
          </span>
        </div>
      </div>
      <div className="ml-2">
        {cert.daysUntilDue !== null && (
          <Badge
            variant="outline"
            className={`text-[10px] h-5 ${statusInfo.bgColor} ${statusInfo.textColor}`}
          >
            {cert.daysUntilDue < 0
              ? `${Math.abs(cert.daysUntilDue)}d overdue`
              : `${cert.daysUntilDue}d`}
          </Badge>
        )}
      </div>
    </div>
  );
}
